import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API_ENDPOINTS } from "../../../constants/config";

function ManageFees() {
  const buttonStyle = "bg-[#0263cc] hover:bg-blue-700 text-white";
  const [savedMessage, setSavedMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [feeTypes, setFeeTypes] = useState([]);
  const [newFeeType, setNewFeeType] = useState({ name: "", amount: "", description: "" });
  const [busRoutes, setBusRoutes] = useState([]); // Make sure it's initialized as an array
  const [selectedFeeType, setSelectedFeeType] = useState(null);
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [multipliers, setMultipliers] = useState({ q1: 2, q2: 3, q3: 3, q4: 3 });

  useEffect(() => {
    fetchFeeTypes();
    fetchBusRoutes();
    fetchMultipliers();
  }, []);

  const fetchFeeTypes = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.busFeesTypes, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setFeeTypes(data);
      }
    } catch (error) {
      console.error("Error fetching fee types:", error);
    }
  };

  const fetchBusRoutes = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const schoolId = user.school;
      
      const response = await fetch(API_ENDPOINTS.vehicleRoutesBySchool(schoolId), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        console.log("Fetched vehicle routes:", data.data.vehicleRoutes); 
        // Ensure we're setting an array, even if empty
        setBusRoutes(Array.isArray(data.data.vehicleRoutes) ? data.data.vehicleRoutes : []);
        console.log("Vehicle routes state updated:", busRoutes);
      }
    } catch (error) {
      console.error("Error fetching vehicle routes:", error);
      setBusRoutes([]); // Set empty array on error
    }
  };

  const fetchMultipliers = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.busFeesMultiplier, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
      });
      console.log("Fetched multipliers:", response);
      if (response.ok) {
        const data = await response.json();
        setMultipliers(data);
      }
    } catch (error) {
      console.error("Error fetching multipliers:", error);
    }
  };

  const handleAddFeeType = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.busFeesTypes, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
        body: JSON.stringify(newFeeType),
      });

      if (response.ok) {
        setSavedMessage("Fee type added successfully!");
        setShowMessage(true);
        setNewFeeType({ name: "", amount: "", description: "" });
        await fetchFeeTypes();
      } else {
        throw new Error("Failed to add fee type");
      }
    } catch (error) {
      setSavedMessage("Failed to add fee type");
      setShowMessage(true);
    } finally {
      setLoading(false);
      setTimeout(() => setShowMessage(false), 3000);
    }
  };

  const handleDeleteFeeType = async (id) => {
    try {
      const response = await fetch(API_ENDPOINTS.busFeesType(id), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
      });

      if (response.ok) {
        setSavedMessage("Fee type deleted successfully!");
        setShowMessage(true);
        await fetchFeeTypes();
      } else {
        throw new Error("Failed to delete fee type");
      }
    } catch (error) {
      setSavedMessage("Failed to delete fee type");
      setShowMessage(true);
    }
    setTimeout(() => setShowMessage(false), 3000);
  };

  const handleLinkFeeType = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.busFeesLink, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
        body: JSON.stringify({
          feeTypeId: selectedFeeType,
          busRouteIds: selectedRoutes,
        }),
      });

      if (response.ok) {
        setSavedMessage("Fee type linked to routes successfully!");
        setShowMessage(true);
        setSelectedFeeType(null);
        setSelectedRoutes([]);
        await fetchBusRoutes();
      }
    } catch (error) {
      setSavedMessage("Failed to link fee type");
      setShowMessage(true);
    }
    setTimeout(() => setShowMessage(false), 3000);
  };

  const handleUpdateMultipliers = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.busFeesMultiplier, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
        body: JSON.stringify(multipliers),
      });

      if (response.ok) {
        setSavedMessage("Multipliers updated successfully!");
        setShowMessage(true);
      } else {
        throw new Error("Failed to update multipliers");
      }
    } catch (error) {
      setSavedMessage("Failed to update multipliers");
      setShowMessage(true);
    } finally {
      setLoading(false);
      setTimeout(() => setShowMessage(false), 3000);
    }
  };

  return (
    <div className="space-y-6">
      {showMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
          {savedMessage}
        </div>
      )}
      
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-lg font-semibold">Manage Bus Fee Types</h4>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              type="text"
              placeholder="Fee Type Name"
              value={newFeeType.name}
              onChange={(e) => setNewFeeType({...newFeeType, name: e.target.value})}
            />
            <Input
              type="number"
              placeholder="Amount"
              value={newFeeType.amount}
              onChange={(e) => setNewFeeType({...newFeeType, amount: e.target.value})}
            />
            <Input
              type="text"
              placeholder="Description (optional)"
              value={newFeeType.description}
              onChange={(e) => setNewFeeType({...newFeeType, description: e.target.value})}
            />
          </div>

          <Button 
            onClick={handleAddFeeType}
            className={buttonStyle}
            disabled={loading}
          >
            {loading ? "Adding..." : "Add Fee Type"}
          </Button>

          <div className="mt-6">
            <h5 className="font-medium mb-4">Current Fee Types monthly</h5>
            <div className="space-y-2">
              {feeTypes.map((feeType) => (
                <div key={feeType._id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <div>
                    <span className="font-medium">{feeType.name}</span>
                    <span className="mx-2">-</span>
                    <span>₹{feeType.amount}</span>
                    {feeType.description && (
                      <p className="text-sm text-gray-600">{feeType.description}</p>
                    )}
                  </div>
                  <Button
                    variant="destructive"
                    onClick={() => handleDeleteFeeType(feeType._id)}
                  >
                    Delete
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow mt-6">
          <h5 className="font-medium mb-4">Quarterly Fee Multipliers</h5>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm mb-1">Q1 Multiplier</label>
              <Input
                type="number"
                value={multipliers.q1}
                onChange={(e) => setMultipliers({...multipliers, q1: parseFloat(e.target.value)})}
              />
            </div>
            <div>
              <label className="block text-sm mb-1">Q2 Multiplier</label>
              <Input
                type="number"
                value={multipliers.q2}
                onChange={(e) => setMultipliers({...multipliers, q2: parseFloat(e.target.value)})}
              />
            </div>
            <div>
              <label className="block text-sm mb-1">Q3 Multiplier</label>
              <Input
                type="number"
                value={multipliers.q3}
                onChange={(e) => setMultipliers({...multipliers, q3: parseFloat(e.target.value)})}
              />
            </div>
            <div>
              <label className="block text-sm mb-1">Q4 Multiplier</label>
              <Input
                type="number"
                value={multipliers.q4}
                onChange={(e) => setMultipliers({...multipliers, q4: parseFloat(e.target.value)})}
              />
            </div>
          </div>
          <Button 
            onClick={handleUpdateMultipliers}
            className={buttonStyle}
            disabled={loading}
          >
            Update Multipliers
          </Button>
        </div>

        <div className="mt-8">
          <h5 className="font-medium mb-4">Link Fee Types to Vehicle Routes</h5>
          <div className="space-y-4">
            <select
              className="w-full p-2 border rounded"
              value={selectedFeeType || ""}
              onChange={(e) => setSelectedFeeType(e.target.value)}
            >
              <option value="">Select Fee Type</option>
              {feeTypes.map((type) => (
                <option key={type._id} value={type._id}>
                  {type.name} - ₹{type.amount}
                </option>
              ))}
            </select>

            <div className="max-h-60 overflow-y-auto border rounded p-2">
              {Array.isArray(busRoutes) && busRoutes.length > 0 ? (
                busRoutes.map((route) => (
                  <div key={route._id} className="flex items-center space-x-2 p-2">
                    <input
                      type="checkbox"
                      checked={selectedRoutes.includes(route._id)}
                      onChange={() => {
                        setSelectedRoutes(
                          selectedRoutes.includes(route._id)
                            ? selectedRoutes.filter(id => id !== route._id)
                            : [...selectedRoutes, route._id]
                        );
                      }}
                    />
                    <label>
                      Vehicle: {route.vehicle?.vehicleNumber || 'Unknown'} - Type: {route.vehicle?.type || 'Unknown'}
                      {route.feeType && (
                        <span className="text-sm text-gray-500">
                          (Current: {feeTypes.find(f => f._id === route.feeType)?.name})
                        </span>
                      )}
                    </label>
                  </div>
                ))
              ) : (
                <div className="text-gray-500 p-2">No vehicle routes available</div>
              )}
            </div>

            <Button
              onClick={handleLinkFeeType}
              disabled={!selectedFeeType || selectedRoutes.length === 0}
              className={buttonStyle}
            >
              Link Fee Type to Selected Routes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManageFees;